import React, { Fragment, PureComponent } from 'react';
import _ from 'lodash';
import { Dialog } from '@mui/material';
import { connect } from 'react-redux';
import { IDispatchProps, IProps, IStateProps } from './interfaces';
import {
  ActionContainer,
  DialogActionsOverride,
  DialogContentContainer,
  DialogHeader,
  DialogHeaderTitle,
  SelectedAssetsContainer,
  SelectedAssetsInfoText,
  SelectedAssetsItemList,
  SelectedAssetText,
  AssetCategoryTitle,
  AssetPropertyRow,
  EditAssetDetailsContainer,
} from './styles';
import { VaultButton } from '../../../Shared';
import { IAppState } from '../../../../state/reducers';
import {
  toggleBulkEditAssets,
  addAssetUpdates,
  bulkUpdate,
} from '../../../../state/bulkUpdateAssets';
import { AssetFieldWrapper } from '../../../Shared/AssetDetails';
import { canUploadAdditionalAttachments } from '../../../../utils/exchange';

class BulkEditDialog extends PureComponent<IProps> {
  private static readonly bulkEditText = 'Bulk Edit';
  private static readonly updateText = 'Update';
  private static readonly cancelText = 'Cancel';

  closeDialogBox = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { toggleBulkEdit } = this.props;
    toggleBulkEdit(false, []);
  };

  handleBulkUpdate = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const { bulkUpdate, onBulkUpdateCallback } = this.props;
    bulkUpdate(onBulkUpdateCallback);
  };

  onFieldChange = (update: {}) => {
    const { addAssetUpdates } = this.props;
    addAssetUpdates(update);
  };

  generateAssetDetails = () => {
    const { editableMetaTags, exchangeSport } = this.props;
    return editableMetaTags.map((category: any, index: number) => {
      if (category.category) {
        const isEditableCategory = category.tags.find((tag: any) => tag.isEditable === 2);
        const displayCategory = category.category.replace(/([A-Z])/g, ' $1').trim();
        if (isEditableCategory) {
          return (
            <Fragment key={`Asset.Details.${index}`}>
              <AssetCategoryTitle
                id={`AssetDetails.AssetInfo.Metadata.Category.${category.category}`}
              >
                {displayCategory}
              </AssetCategoryTitle>
              {category.tags.map((tag: any, tagsIndex: number) => {
                const {
                  tagName, dataType, displayName, value, lookupObjectId, isEditable,
                } = tag;

                if (isEditable !== 2 || tagName === 'title') return null;

                // Hide attachments for sports other than Lacrosse
                if (tagName === 'attachments' && !canUploadAdditionalAttachments(exchangeSport)) return null;

                // Clean up default values (instead of undefined, as logic within components can't handle it)
                let _value = value;
                if (tagName === 'securityProfiles') {
                  _value = _value || [];
                }
                if (tagName === 'owner') {
                  _value = _value || '';
                }

                return (
                  <AssetPropertyRow key={`${tagName}.AssetEditable`}>
                    <AssetFieldWrapper
                      assetType="Video"
                      tagName={tagName}
                      data-tag={tagName}
                      dataType={dataType}
                      data-type={dataType}
                      label={displayName}
                      value={_value}
                      data-value={_value}
                      lookupObjectId={lookupObjectId}
                      disabled={isEditable !== 2}
                      allowAddRemove={false}
                      // track where to dispatch new value in `assetDetails`
                      track={{
                        category: category.category,
                        tagsId: tag._id,
                      }}
                      isBulkEdit
                      handleChangeCallback={this.onFieldChange}
                    />
                  </AssetPropertyRow>
                );
              })}
            </Fragment>
          );
        }
      }
      return null;
    });
  };

  render() {
    const {
      selectedAssets, isBulkEditButtonClicked, isUpdatingInProgress,
    } = this.props;
    return (
      <Dialog open={isBulkEditButtonClicked} fullWidth maxWidth="md">
        <DialogHeader
          data-auto="BulkEditModal"
        >
          <DialogHeaderTitle>
            {BulkEditDialog.bulkEditText}
          </DialogHeaderTitle>
        </DialogHeader>
        <DialogContentContainer>
          <SelectedAssetsContainer>
            <SelectedAssetsInfoText>Bulk editing {selectedAssets.length} Items</SelectedAssetsInfoText>
            <SelectedAssetsItemList>
              {selectedAssets.map(((s: any) => <SelectedAssetText key={s._source.title}>{s._source.title}</SelectedAssetText>))}
            </SelectedAssetsItemList>
          </SelectedAssetsContainer>

          <EditAssetDetailsContainer className="AssetDetails">
            {this.generateAssetDetails()}
          </EditAssetDetailsContainer>
        </DialogContentContainer>
        <DialogActionsOverride disableSpacing>
          <ActionContainer>
            <VaultButton
              id={BulkEditDialog.cancelText}
              variant="plainOnPaper"
              onClick={this.closeDialogBox}
              data-auto="BulkCancelBtn"
            >
              {BulkEditDialog.cancelText}
            </VaultButton>
            <VaultButton
              id={BulkEditDialog.updateText}
              disabled={(selectedAssets.length === 0 || isUpdatingInProgress)}
              variant="primaryOnWhite"
              onClick={this.handleBulkUpdate}
              data-auto="BulkSaveBtn"
            >
              {BulkEditDialog.updateText}
            </VaultButton>
          </ActionContainer>
        </DialogActionsOverride>
      </Dialog>
    );
  }
}
const mapStateToProps = (state: IAppState): IStateProps => ({
  isBulkEditButtonClicked: state.bulkEditAssetsDialog.isBulkEditButtonClicked,
  selectedAssets: state.bulkEditAssetsDialog.selectedAssets,
  editableMetaTags: state.bulkEditAssetsDialog.editableMetaTags,
  isUpdatingInProgress: state.bulkEditAssetsDialog.isUpdatingInProgress,
  exchangeSport: state.exchange.initExchange.sport,
});

const mapDispatchToProps: IDispatchProps = {
  toggleBulkEdit: toggleBulkEditAssets,
  addAssetUpdates,
  bulkUpdate,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkEditDialog);
